import React, { Fragment, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import { Container, Grid, Typography } from '@mui/material';
import FunctionCardsContainer, { FUNCTION_CARD_COLUMN } from '../../../components/FunctionCard/FunctionCardsContainer';
import PolicyReviewChooseClient from '../../PolicyReview/PolicyReviewChooseClient/PolicyReviewChooseClient';
import { updateViewingClient } from '../../../store/client/duck';
import ChatGPTIGPostDrawer from '../../../components/ClientEntry/ChatGPTPresetDrawer/ChatGPTIGPostDrawer/ChatGPTIGPostDrawer';
import { toggleControl, toggleDialog } from '../../../store/control/duck';
import { isClientDisconnected, toggleMeetingNoteDialog } from '../../../components/ClientEntry/utils';
import { tracking } from '../../../utils/tracking';
import { ChatGPTRewriteMessageDrawer } from '../../../components/ClientEntry/ChatGPTPresetDrawer/ChatGPTRewriteMessage';
import NativeOrWeb from '../../../utils/native';
import { toast } from 'react-toastify';
import IconToast from '../../../components/NewToast';
import ChatGPTPersonalAnalysis from '../../../components/ClientEntry/ChatGPTPresetDrawer/ChatGPTPersonalAnalysis/ChatGPTPersonalAnalysis';
import ChatGPTIceBreakingDrawerV2 from '../../../components/ClientEntry/ChatGPTPresetDrawer/ChatGPTIceBreakingDrawerV2/ChatGPTIceBreakingDrawerV2';
import './ChatGPTOptions.scss';
import store from '../../../store';
import { hasCampaignCode } from '../../../utils/user';

import { shouldEnableAiCoach, shouldEnableChatGPT, shouldEnableTeamChatBot } from '../../../utils/user';
import GPTProductRecommendationDrawerV2 from '../../../components/ClientEntry/ChatGPTPresetDrawer/GPTProductRecommendationV2/GPTProductRecommendationV2.jsx';
import ChatGPTEventRecommendationDrawer from '../../../components/ClientEntry/ChatGPTPresetDrawer/ChatGPTEventRecommendationDrawer/ChatGPTEventRecommendationDrawer';
import ChatGPTPersonalMessageDrawer from '../../../components/ClientEntry/ChatGPTPresetDrawer/ChatGPTPersonalMessageDrawer/ChatGPTPersonalMessageDrawer';
import ChatBotDialog from '../../../components/ChatBot/ChatBotDialog';
import { clientPath } from '../../../utils/client-route';
import { clientToView } from '../../../utils/viewing-client';
// lucide, as ClientV2 uses, rather than an emoji in the tile: a colour emoji on
// a solid blue ground reads as a smudge at 22px, and the point of borrowing
// ClientV2's heading was to look like the same app.
import { ClipboardCheck, FolderOpen, Handshake } from 'lucide-react';

/**
 * The side-bar colours every card page cycles through: 會面工具, 客戶開發, 招募
 * and 分析 all run 青 → 淺藍 → 深藍 → 紫. This page was the one that had drifted
 * to six colours in no order, plus a grey for "needs a client" that the
 * 「👤 需選客戶」 chip already says, and that read as "not enabled".
 */
const CARD_COLORS = ['#5fc3d2', '#3999f8', '#2260a5', '#483fd2'];

const ChatGPTOptions = props => {
  const {
    intl,
    history,
    viewingClient,
    clientData,
    updateViewingClient,
    toggleControl,
    toggleDialog,
    userDetails,
    teamCampaignCodes,
    userSubscription
  } = props;

  // This page reads its client from redux, which a reload empties — leaving
  // the stored id as the only record of who the adviser came here for. Without
  // this the page kept whoever redux still held (or nobody), which is the
  // exact drift `clientToView` exists to undo.
  // once and settles.
  const storedClientId = window.localStorage.getItem('clientId');
  const clientId = storedClientId ? JSON.parse(storedClientId) : undefined;

  // Resolved ONCE per mount, and only into a client this page can actually
  // serve. Both halves are load-bearing:
  //
  //  - Once, because `viewingClient` is in the deps and the page's own picker
  //    writes only redux (`onSelectClient`) — never the stored id. Re-running
  //    after a pick would resolve the STORED client again and snap the picker
  //    back, leaving the adviser unable to change client on this page at all.
  //  - Not disconnected, because PolicyReviewChooseClient clears such a client
  //    on sight (its own effect). Re-resolving what it just cleared is an
  //    endless set/clear alternation between the two effects, and both write
  //    real redux state, so React re-renders until it gives up.
  //
  // The flag is set as soon as a client is FOUND, not when one is dispatched:
  // a disconnected match is still an answer, and asking again cannot change it.
  const resolvedRef = useRef(false);

  useEffect(() => {
    if (resolvedRef.current) return;
    const client = clientToView(clientData, viewingClient, clientId);
    if (!client) return;
    resolvedRef.current = true;
    if (isClientDisconnected(client)) return;
    updateViewingClient(client);
  }, [clientData, viewingClient, clientId, updateViewingClient]);

  useEffect(() => {
    const showChatGPT = shouldEnableChatGPT({
      user: userDetails || {},
      teamCampaignCodes: teamCampaignCodes,
      userSubscription: userSubscription
    });
    if (!showChatGPT) {
      history.push('/home');
    }
  }, []);

  const onSelectClient = client => {
    updateViewingClient(client);
  };

  /**
   * Was this page opened for a recruitment conversation?
   *
   * Read from the location the adviser arrived on, and nowhere else. Every
   * existing door — Home, the client page, the Linker header, a bookmark —
   * carries no `mode`, so they all get exactly the page they got before this
   * existed. Only ClientV2's 招募 view sets it (clientActions.tsx, 'ai-recruit',
   * and the disclaimer dialog on the first ever visit).
   *
   * Not held in redux and not written to the URL: it describes the trip, not
   * the adviser. Coming back here from Home later should be the ordinary page,
   * and it is.
   */
  const recruitMode = history.location?.state?.mode === 'recruit';

  /**
   * 招募 mode hides the sales-only tools rather than reordering them, which is
   * the one thing that could read as "my features are gone". This is the way
   * back — in place, keeping the trip's recruitment framing, rather than a link
   * that throws the adviser onto a different page.
   */
  const [showAllAi, setShowAllAi] = useState(false);

  /**
   * 團隊Bot is a redux dialog, not a route: the card only dispatches `chatBot`,
   * so the page that offers the card is the page that has to mount it. Every
   * other door already does — Home, 招募, and ClientV2's drawer — and this one
   * did not, which is why the tile lit up, recorded the click, and opened
   * nothing at all.
   *
   * Held in a const because one answer decides two things that must not drift
   * apart: whether the card is offered, and whether the dialog it opens is on
   * the page to be opened. `recruitMode` leads, so the 業務 page asks the
   * subscription helper nothing at all — it has no way to reach either.
   */
  const showTeamBot =
    recruitMode && shouldEnableTeamChatBot({ user: userDetails, teamCampaignCodes, userSubscription });

  const igPost = () => {
    tracking('Click on Social Media Post - ChatGPT');

    // `preset` saves the one step that made the recruitment copy unfindable:
    // it has always lived behind a radio inside this drawer, and nothing on the
    // way in ever mentioned it. The drawer still shows the radio, so the choice
    // stays the adviser's — this only changes which option is already on.
    toggleControl('chatGPTIGPostDrawer', {
      client: viewingClient,
      preset: recruitMode ? 'recruit' : undefined
    });
  };

  const iceBreaking = () => {
    if (!viewingClient) {
      return;
    }

    tracking('Click on IceBreaking Script - ChatGPT');

    history.push({
      pathname: clientPath(),
      state: {
        selectedClient: viewingClient,
        gpt: 'ice-breaking'
      }
    });
  };

  const personalAnalysis = () => {
    tracking('Click on Personal Analysis - ChatGPT');

    toggleControl('ChatGPTPersonalAnalysis', { client: viewingClient });
  };

  const personalMsg = () => {
    if (!viewingClient) {
      return;
    }

    tracking('Click on Personalized Message - ChatGPT');

    // Opened in place, like every other card on this page. It used to route to
    // /emergency_reference first, because that view was the only one mounting
    // this drawer — so closing it left the adviser on 救命紙 rather than back
    // on the list they came from, and the browser Back button returned them to
    // a page they never asked for. Mounting the drawer here (below) is what
    // makes the navigation unnecessary; the drawer needs nothing of 救命紙's,
    // it takes the client from this call and its output is a copy box.
    toggleControl('chatGPTPersonalMessageDrawer', { client: viewingClient });
  };

  const voiceNotes = () => {
    if (!viewingClient) {
      return;
    }

    tracking('Click on Voice Notes - ChatGPT');

    toggleMeetingNoteDialog(toggleDialog, viewingClient, { triggerRecordDialog: true });
    history.push({ pathname: clientPath(), selectedClient: viewingClient, gpt: true });
  };

  const rewriteMessage = () => {
    tracking('Click on RewriteMessage - ChatGPT');
    toggleControl('chatGPTRewriteMessageDrawer', {
      open: true,
      message: '',
      client: viewingClient,
      onValue: value => {
        NativeOrWeb.copyToClipboard(value)
          .then(() => {
            toast.info(IconToast('info', intl.formatMessage({ id: 'Copied!' })), { className: 'new-toast' });
          })
          .catch(console.log);
      }
    });
  };

  const productRecommendationDrawerV2 = () => {
    if (!viewingClient) {
      return;
    }

    tracking('Click on Product Recommendation - ChatGPT');
    toggleControl('gptProductRecommendationV2Drawer', { client: viewingClient, open: true });
  };

  const eventRecommendation = () => {
    if (!viewingClient) {
      return;
    }

    tracking('Click on Event Recommendation - ChatGPT');
    toggleControl('chatGPTEventRecommendationDrawer', { client: viewingClient, open: true });
  };
  // const newsRecommendationForClient = () => {
  //   // tracking('Click on News Recommendation for Client - ChatGPT');
  //     if (!viewingClient) {
  //       return;
  //     }

  //     tracking('Click on IceBreakingV2 - ChatGPT');
  //     toggleControl('chatGPTIceBreakingV2Drawer', { client: viewingClient });

  //     history.push({
  //       pathname: '/client',
  //       state: {
  //         selectedClient: viewingClient,
  //         gpt: 'ice-breaking-V2'
  //       }
  //     });
  // };

  // const productRecomment = () => {
  //   tracking('Click on Product Recommendation - ChatGPT');
  //   toggleControl('gptProductRecommendationDrawer', {
  //     open: true,
  //     showSendButton: true,
  //     client: viewingClient
  //   });
  // }

  const needToSelectClient = func => {
    if (!viewingClient) {
      return () => toast.info(IconToast('info', intl.formatMessage({ id: 'please-select-client-first' })));
    }

    return func;
  };

  const newsMatchingDrawer = () => {
    if (!viewingClient) {
      return () => toast.info(IconToast('info', intl.formatMessage({ id: 'please-select-client-first' })));
    }

    store.dispatch(toggleControl('chatGPTIceBreakingDrawer', { client: viewingClient }));
  };

  /**
   * A card's title, with 「👤 需選客戶」 appended while no client is chosen.
   *
   * The page has always had two kinds of card — six that need a client and
   * three that do not — and has always said so only by greying the first kind
   * out. That grey is unreadable: it is the same grey a card wears when the
   * feature is not enabled for this adviser at all, so "needs a client" and
   * "you do not have this" look identical, and the only way to tell them apart
   * was to tap and be told off by a toast.
   *
   * The grouping below is by WHEN an adviser reaches for a tool, not by whether
   * it needs a client, so it deliberately does not answer this — hence the chip.
   * It disappears the moment a client is picked, which also gives the client
   * picker some visible answer to having been used; until now it had none.
   *
   * `FunctionCard` renders a string `text` inside its own Typography and any
   * other node as-is, so the annotated form has to bring that Typography with
   * it — which is why a bare label is returned whenever there is nothing to
   * annotate, rather than wrapping every card's title for the sake of two.
   */
  const cardText = (labelId, needsClient, recruitTag) => {
    const label = intl.formatMessage({ id: labelId });
    const showNeedsClient = !!needsClient && !viewingClient;

    if (!recruitTag && !showNeedsClient) {
      return label;
    }

    return (
      <Typography className="title" align="left">
        {label}
        {recruitTag && <span className="gpt-recruit-chip">💼 {intl.formatMessage({ id: 'gpt-recruit-tag' })}</span>}
        {showNeedsClient && (
          <span className="gpt-needs-client">👤 {intl.formatMessage({ id: 'gpt-needs-client' })}</span>
        )}
      </Typography>
    );
  };

  /**
   * The two cards that exist ONLY in 招募 mode.
   *
   * Both are established surfaces reached from somewhere else today — AI Coach
   * from Home and the Linker header, 團隊Bot from the 招募 page and ClientV2's
   * drawer — and both are things an adviser recruiting someone actually wants:
   * one to rehearse the conversation, one to answer the questions a candidate
   * asks about the company. Neither belongs on the ordinary 業務 page, so they
   * are not added to it; they appear only when this page was opened for
   * recruitment, and only if this adviser has them at all.
   *
   * Their navigation is lifted verbatim from clientActions.tsx rather than
   * written again, so the two doors onto each cannot drift apart.
   *
   * Not built at all outside 招募 mode, rather than built and then filtered
   * out: building them costs two campaign-code lookups and a subscription
   * object on every render of the ordinary 業務 page, which can never show
   * them. The guard leaves `false` in the array, and `inScope` already drops
   * a falsy item — which is what that check was there for.
   */
  const AI_COACH_CARD = recruitMode && {
    text: cardText('clientv2-recruit-ai-coach', false),
    subtitle: intl.formatMessage({ id: 'gpt-ai-coach-desc' }),
    imgSrc: 'img/icons/ai_coach_active_color.svg',
    imgAlt: 'ai-coach',
    onClick: () => {
      tracking('Click on AI Coach - ChatGPT');
      history.push('/new/media-analysis');
    },
    recruit: true,
    recruitOnly: true,
    // Two generations of campaign code name the same feature: Home reads
    // FEATURE_AICOACH, the Linker header reads FEATURE_AIDRILL. ClientV2's
    // drawer accepts either, and so does this.
    disabled: !(
      shouldEnableAiCoach({ user: userDetails, teamCampaignCodes }) ||
      hasCampaignCode(userDetails, teamCampaignCodes, 'FEATURE_AIDRILL')
    )
  };

  const TEAM_BOT_CARD = recruitMode && {
    text: cardText('clientv2-recruit-team-bot', false),
    subtitle: intl.formatMessage({ id: 'gpt-team-bot-desc' }),
    imgSrc: 'img/icons/message_people_active_color.svg',
    imgAlt: 'team-bot',
    onClick: () => {
      tracking('Click on Team Bot - ChatGPT');
      toggleDialog('chatBot', {});
    },
    recruit: true,
    recruitOnly: true,
    disabled: !showTeamBot
  };

  /**
   * 見面前 — everything whose output is a reason to get in front of someone: a
   * topic, a message, a piece of public content, an event to invite them to.
   *
   * 熱話推薦 and 破冰話術 are mutually exclusive (FEATURE_NEWSMATCH turns the
   * first on and the second off), so they share the leading slot rather than
   * both taking one.
   */
  const beforeMeetingItems = [
    {
      text: cardText('chat-gpt-ice-breaking-drawer-v2-title', true),
      subtitle: intl.formatMessage({ id: 'chat-gpt-ice-breaking-drawer-v2-desc' }),
      imgSrc: 'img/hot_news_match_icon.svg',
      imgAlt: 'news-matching',
      onClick: needToSelectClient(newsMatchingDrawer),
      recruit: false,
      disabled:
        !hasCampaignCode(userDetails, teamCampaignCodes, 'FEATURE_GPT') ||
        !hasCampaignCode(userDetails, teamCampaignCodes, 'FEATURE_NEWSMATCH')
    },
    {
      text: cardText('chat-gpt-options-ice-breaking', true),
      subtitle: intl.formatMessage({ id: 'chat-gpt-options-ice-breaking-desc' }),
      imgSrc: 'img/icons/speaker_star_active_color.svg',
      imgAlt: 'ice-breaking',
      onClick: needToSelectClient(iceBreaking),
      recruit: false,
      disabled: hasCampaignCode(userDetails, teamCampaignCodes, 'FEATURE_NEWSMATCH')
    },
    {
      text: cardText('chat-gpt-options-personal-msg', true),
      subtitle: intl.formatMessage({ id: 'chat-gpt-options-personal-msg-desc' }),
      imgSrc: 'img/icons/personal_message_active_color.svg',
      imgAlt: 'personal-msg',
      onClick: needToSelectClient(personalMsg),
      recruit: false
    },
    {
      text: cardText('chat-gpt-options-ig-post', false, recruitMode),
      subtitle: intl.formatMessage({ id: 'chat-gpt-options-ig-post-desc' }),
      imgSrc: 'img/icons/video_script_active_color.svg',
      imgAlt: 'ig-post',
      onClick: igPost,
      recruit: true
    },
    AI_COACH_CARD,
    // An event is a reason to ask someone out, which is why it leads a
    // conversation rather than following one.
    {
      text: cardText('chat-gpt-options-event-recommendation-title', true),
      subtitle: intl.formatMessage({ id: 'chat-gpt-options-event-recommendation-desc' }),
      imgSrc: 'img/event_match_icon.svg',
      imgAlt: 'event-recommendation',
      onClick: needToSelectClient(eventRecommendation),
      recruit: false,
      disabled: !hasCampaignCode(userDetails, teamCampaignCodes, 'FEATURE_EVENT')
    }
  ];

  /**
   * 見面後 — everything that only pays off once a conversation has happened:
   * send them material, suggest a plan, write down what was said, tidy the
   * follow-up message.
   *
   * 改寫訊息 is here rather than above because 個性化訊息 already covers
   * reaching out cold; what this one is actually used for is polishing what you
   * send afterwards.
   */
  const afterMeetingItems = [
    {
      text: cardText('chat-gpt-options-lazy-packs', false),
      subtitle: intl.formatMessage({ id: 'chat-gpt-options-lazy-packs-desc' }),
      imgSrc: 'img/icons/lazypack_good_active_color.svg',
      imgAlt: 'lazy-packs',
      onClick: personalAnalysis,
      recruit: false
    },
    {
      text: cardText('gpt-options-product-recommendation-title', true),
      subtitle: intl.formatMessage({ id: 'gpt-options-product-recommendation-desc' }),
      imgSrc: 'img/product_match_icon.svg',
      imgAlt: 'product-recommendation',
      onClick: needToSelectClient(productRecommendationDrawerV2),
      recruit: false,
      disabled: !hasCampaignCode(userDetails, teamCampaignCodes, 'FEATURE_PRODUCT')
    },
    {
      text: cardText('chat-gpt-options-voice-notes', true),
      subtitle: intl.formatMessage({ id: 'chat-gpt-options-voice-notes-desc' }),
      imgSrc: 'img/icons/voice_star_active_color.svg',
      imgAlt: 'voice-notes',
      onClick: needToSelectClient(voiceNotes),
      recruit: true
    },
    {
      text: cardText('gpt-message-rewrite-title', false),
      subtitle: intl.formatMessage({ id: 'gpt-message-rewrite-description' }),
      imgSrc: 'img/icons/edit_active_color.svg',
      imgAlt: 'emojis',
      onClick: rewriteMessage,
      recruit: true
    },
    TEAM_BOT_CARD
  ];

  /**
   * Which cards this trip is allowed to show, before the campaign codes get
   * their say.
   *
   * Outside 招募 mode this is everything the page has always had — the two
   * recruitment-only cards are the only things it removes, and they were never
   * on this page to begin with.
   */
  const inScope = item => {
    if (!item) return false;
    if (!recruitMode) return !item.recruitOnly;
    return true;
  };

  /**
   * One group, or nothing at all when every card in it is withheld from this
   * adviser. A heading over an empty stretch of page would be worse than the
   * flat list this replaces — it would name a thing that is not there.
   *
   * `FunctionCardsContainer` is called directly, twice, rather than through
   * `FunctionCardsView`: that view renders exactly one container, and it is
   * shared with twelve other pages that want nothing to do with headings.
   * Two calls here cost this file six lines of wrapper and cost those pages
   * nothing.
   */
  const renderGroup = (items, Icon, titleId, quiet, only) => {
    // `recruit` and `recruitOnly` are this page's own bookkeeping and must not
    // travel any further: FunctionCard destructures the props it knows and
    // spreads the rest onto its root `<div>`, so an unrecognised key reaches
    // the DOM and React warns once per card — on the ordinary business page
    // too, since every item carries the flags. Dropped here rather than in
    // FunctionCard, which twelve other pages share.
    //
    // The colour is assigned here, by position among the cards actually shown,
    // rather than written on each item: campaign codes, 招募 mode and the
    // 熱話推薦 / 破冰話術 swap all hide cards, and a colour fixed per item breaks
    // the cycle wherever one is missing. Each group starts the cycle again.
    const visible = items
      .filter(item => inScope(item) && !item.disabled && (!only || only(item)))
      .map(({ recruit, recruitOnly, ...card }, index) => ({
        ...card,
        color: CARD_COLORS[index % CARD_COLORS.length]
      }));

    if (!visible.length) {
      return null;
    }

    return (
      <Grid size={12}>
        <FunctionCardsContainer
          items={visible}
          prepend={
            <div className={`gpt-group-heading${quiet ? ' is-quiet' : ''}`}>
              <span className="gpt-group-icon">
                <Icon size={18} strokeWidth={2.2} />
              </span>
              <span className="gpt-group-title">{intl.formatMessage({ id: titleId })}</span>
              <span className="gpt-group-count">
                {intl.formatMessage({ id: 'gpt-group-count' }, { count: visible.length })}
              </span>
            </div>
          }
        />
      </Grid>
    );
  };

  return (
    <Fragment>
      <ChatGPTPersonalAnalysis />
      <ChatGPTIGPostDrawer />
      <ChatGPTRewriteMessageDrawer />
      <ChatGPTIceBreakingDrawerV2 />
      <GPTProductRecommendationDrawerV2 />
      <ChatGPTEventRecommendationDrawer />
      <ChatGPTPersonalMessageDrawer />
      {/* Mounted only where the card that opens it can appear: the card is
          招募-only, and the ordinary 業務 page should not carry a dialog that
          nothing on it can reach — `showTeamBot` already folds that in. */}
      {showTeamBot && <ChatBotDialog promptIdentifier="team_bot" mode="team" />}
      <Container className="nav-pad chat-gpt-options">
        {/* rowSpacing 2 rather than spacing 3: the 24px row gap sat on top of the
            group heading's own 12px top margin, so the first card started 36px
            below the client picker. */}
        <Grid container rowSpacing={2} columnSpacing={3} sx={{ alignItems: 'center' }}>
          {/* The page's heading block: the hint, the client picker, and in 招募
              mode the context bar. Laid out in the same column as the cards
              below so the left edges line up, but rendered as itself rather
              than as an empty card list with a prepend.

              Kept to two short rows. It used to be a three-sentence paragraph
              ending in 「請選擇客戶:」 over a half-width select — four lines and a
              spare row before the first card on a phone. The notice is one line
              of small print now, and the prompt is the picker's own label. */}
          <Grid size={12}>
            <Grid container spacing={2} style={FUNCTION_CARD_COLUMN}>
              <Grid size={12}>
                <div className="gpt-options-header">
                  <Typography variant="caption" component="p" color="textSecondary">
                    {intl.formatMessage({ id: 'chat-gpt-options-hint-short' })}
                  </Typography>
                  <div className="gpt-client-row">
                    <Typography component="span" className="gpt-client-label">
                      {intl.formatMessage({ id: 'chat-gpt-options-client-label' })}
                    </Typography>
                    <div className="gpt-client-picker">
                      <PolicyReviewChooseClient
                        selectedClient={viewingClient || ''}
                        setSelectedClient={onSelectClient}
                      />
                    </div>
                  </div>
                  {recruitMode && (
                    <div className="gpt-recruit-bar-wrap">
                      {/* Says what the page has done to itself, and undoes it.
                          Without this the filtering is indistinguishable from
                          features having disappeared — which is the whole risk
                          of hiding rather than reordering. No candidate name:
                          the client picker directly above already shows it. */}
                      <div className="gpt-recruit-bar">
                        <span className="gpt-recruit-bar-mode">
                          💼 {intl.formatMessage({ id: 'gpt-recruit-mode' })}
                        </span>
                        {/* A real button, not a styled span: this is the only
                            way back to the half of the page 招募 mode hides, and
                            a span cannot be tabbed to or fired with Enter/Space
                            — a keyboard or screen-reader adviser could not
                            reach those tools at all. Painted back down to the
                            link it looks like, in the stylesheet. */}
                        <button
                          type="button"
                          className="gpt-recruit-bar-toggle"
                          aria-pressed={showAllAi}
                          onClick={() => setShowAllAi(!showAllAi)}
                        >
                          {intl.formatMessage({ id: showAllAi ? 'gpt-show-recruit-only' : 'gpt-show-all-ai' })}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </Grid>
            </Grid>
          </Grid>
          {/* In 招募 mode the two groups keep their headings and their meaning —
              an adviser switching between the two conversations should not have
              to learn a second layout — and simply hold fewer cards. What comes
              off is the sales-only half, and it comes back as its own quiet
              group the moment 顯示全部 AI is tapped. */}
          {renderGroup(
            beforeMeetingItems,
            Handshake,
            'gpt-group-before-meeting',
            false,
            recruitMode ? item => item.recruit : null
          )}
          {renderGroup(
            afterMeetingItems,
            ClipboardCheck,
            'gpt-group-after-meeting',
            true,
            recruitMode ? item => item.recruit : null
          )}
          {recruitMode &&
            showAllAi &&
            renderGroup(
              [...beforeMeetingItems, ...afterMeetingItems],
              FolderOpen,
              'gpt-group-other-ai',
              true,
              item => !item.recruit
            )}
        </Grid>
      </Container>
    </Fragment>
  );
};

const container = connect(
  state => ({
    viewingClient: state.client.viewingClient,
    clientData: state.client.clientData,
    userDetails: state.user.userDetails,
    teamCampaignCodes: state.functions.campaignCodes,
    userSubscription: state.userSubscription.userSubscription
  }),
  {
    updateViewingClient,
    toggleControl,
    toggleDialog
  }
)(ChatGPTOptions);

export default injectIntl(container);
